import React, { useCallback, useEffect, useState } from "react";
import { Plus, UsersRound, Power, Copy } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { PageHeader } from "@/components/Layout";
import { Chip, EmptyState } from "@/components/Bits";
import { useAuth } from "@/context/AuthContext";
import { useBrand } from "@/context/BrandContext";
import api, { formatApiErrorDetail } from "@/lib/api";

const inputCls = "w-full bg-[#050505] border border-white/10 text-white rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-white transition-colors";
const EMPTY = { name: "", email: "", phone: "", role: "general_staff", designation: "", department: "",
  assigned_brands: [], assigned_hubs: [], password: "", must_change_password: true };

const Multi = ({ options, value, onToggle, testid }) => (
  <div className="flex flex-wrap gap-1.5" data-testid={testid}>
    {options.length === 0 && <span className="text-[11px] text-zinc-600 font-mono">none available</span>}
    {options.map((o) => (
      <button key={o.value} type="button" onClick={() => onToggle(o.value)}
        className={`text-[11px] font-mono px-2 py-1 rounded-sm border ${value.includes(o.value) ? "bg-white text-black border-white" : "border-white/15 text-zinc-400 hover:bg-white/5"}`}>{o.label}</button>
    ))}
  </div>
);

export default function Team() {
  const { user } = useAuth();
  const { brands, hubs } = useBrand();
  const [team, setTeam] = useState([]);
  const [roles, setRoles] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [tempPw, setTempPw] = useState(null);
  const [profile, setProfile] = useState(null);
  const [tab, setTab] = useState("overview");

  const load = useCallback(() => api.get("/employees").then((r) => setTeam(r.data)).catch(() => {}), []);
  useEffect(() => {
    load();
    api.get("/rbac/roles").then((r) => setRoles(r.data)).catch(() => {});
  }, [load]);

  const toggle = (key, v) => setForm((f) => ({ ...f, [key]: f[key].includes(v) ? f[key].filter((x) => x !== v) : [...f[key], v] }));

  const submit = async () => {
    if (!form.name || !form.email) return toast.error("Name and email are required");
    try {
      const r = await api.post("/employees", form);
      setTempPw({ email: form.email, password: r.data.temp_password });
      toast.success("Employee created");
      setForm(EMPTY); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const deactivate = async (id) => {
    try { await api.post(`/employees/${id}/deactivate`); toast.success("Employee deactivated"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const openProfile = (m) => { setProfile(m); setTab("overview"); };

  const roleOpts = roles.filter((r) => !r.is_admin || r.key === "admin");
  const brandOpts = brands.map((b) => ({ value: b.name, label: b.name }));
  const hubOpts = hubs.map((h) => ({ value: h.id, label: h.name }));

  return (
    <div>
      <PageHeader title="Team" subtitle={`${team.length} employees · roles, brands & hubs`}
        actions={<button data-testid="add-member-button" onClick={() => { setForm(EMPTY); setTempPw(null); setOpen(true); }} className="flex items-center gap-2 text-sm bg-white text-black font-medium px-4 py-2 rounded-sm hover:bg-zinc-200 transition-colors"><Plus size={16} /> Add Employee</button>} />
      <div className="p-8">
        {team.length === 0 ? <EmptyState icon={UsersRound} title="No employees" hint="Add your first employee." /> : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {team.map((m) => (
              <div key={m.id} data-testid={`team-card-${m.id}`} onClick={() => openProfile(m)} className="bg-[#121212] border border-white/10 rounded-sm p-5 hover:border-white/25 transition-colors cursor-pointer group">
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 rounded-sm bg-white/10 border border-white/10 flex items-center justify-center font-display font-bold">{m.name.charAt(0)}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <h3 className="font-display text-base font-medium truncate">{m.name}</h3>
                      {m.email !== user?.email && m.role !== "owner" && (
                        <button data-testid={`deactivate-${m.id}`} onClick={(e) => { e.stopPropagation(); deactivate(m.id); }} className="text-zinc-600 hover:text-[#FF6259] opacity-0 group-hover:opacity-100 transition-opacity"><Power size={15} /></button>
                      )}
                    </div>
                    <Chip className="bg-white/5 border-white/15 text-zinc-400 mt-1">{m.role_name || m.role}</Chip>
                    {m.status === "Inactive" && <Chip className="bg-[#FF3B30]/10 border-[#FF3B30]/30 text-[#FF6259] ml-1">Inactive</Chip>}
                  </div>
                </div>
                <div className="mt-4 pt-3 border-t border-white/10 space-y-1.5 text-xs">
                  <div className="font-mono text-zinc-500">{m.employee_id || "—"} · {m.email}</div>
                  <div><span className="text-zinc-600">Brands:</span> <span className="text-zinc-300">{(m.assigned_brands || []).join(", ") || "All / none"}</span></div>
                  <div><span className="text-zinc-600">Active tasks:</span> <span className="text-zinc-300">{m.active_tasks ?? 0}</span></div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add Employee dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#121212] border-white/10 text-white rounded-sm max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-xl">Add Employee</DialogTitle><DialogDescription className="sr-only">Create an employee with a role, brands and hubs.</DialogDescription></DialogHeader>
          {tempPw ? (
            <div className="space-y-4 pt-2" data-testid="temp-password-panel">
              <p className="text-sm text-zinc-300">Employee created. Share these one-time login details — they'll be asked to set a new password on first login.</p>
              <div className="bg-[#050505] border border-white/10 rounded-sm p-3 font-mono text-sm">
                <div className="text-zinc-400">{tempPw.email}</div>
                <div className="flex items-center justify-between mt-1"><span className="text-[#00FF66]">{tempPw.password}</span>
                  <button onClick={() => { navigator.clipboard?.writeText(tempPw.password); toast.success("Copied"); }} className="text-zinc-500 hover:text-white"><Copy size={14} /></button></div>
              </div>
              <button onClick={() => setOpen(false)} className="w-full text-sm bg-white text-black font-medium py-2.5 rounded-sm hover:bg-zinc-200">Done</button>
            </div>
          ) : (
            <div className="space-y-3 pt-2">
              <div className="grid grid-cols-2 gap-3">
                <input data-testid="member-name-input" className={inputCls} placeholder="Full name *" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
                <input data-testid="member-email-input" className={inputCls} placeholder="Email *" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <input className={inputCls} placeholder="Phone" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} />
                <input className={inputCls} placeholder="Designation" value={form.designation} onChange={(e) => setForm((f) => ({ ...f, designation: e.target.value }))} />
              </div>
              <select data-testid="member-role-select" className={inputCls} value={form.role} onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}>
                {roleOpts.map((r) => <option key={r.key} value={r.key}>{r.name} — {r.department || "—"}</option>)}
              </select>
              <div>
                <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-1.5">Assigned brands</div>
                <Multi options={brandOpts} value={form.assigned_brands} onToggle={(v) => toggle("assigned_brands", v)} testid="member-brands" />
              </div>
              <div>
                <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-1.5">Assigned hubs</div>
                <Multi options={hubOpts} value={form.assigned_hubs} onToggle={(v) => toggle("assigned_hubs", v)} testid="member-hubs" />
              </div>
              <input data-testid="member-password-input" className={inputCls} placeholder="Temporary password (blank = auto-generate)" value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} />
              <label className="flex items-center gap-2 text-xs text-zinc-400"><input type="checkbox" checked={form.must_change_password} onChange={(e) => setForm((f) => ({ ...f, must_change_password: e.target.checked }))} /> Require password change on first login</label>
              <button data-testid="member-submit-button" onClick={submit} className="w-full text-sm bg-white text-black font-medium py-2.5 rounded-sm hover:bg-zinc-200">Create Employee</button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Employee profile */}
      <Dialog open={!!profile} onOpenChange={(v) => !v && setProfile(null)}>
        <DialogContent className="bg-[#121212] border-white/10 text-white rounded-sm max-h-[90vh] overflow-y-auto" data-testid="employee-profile">
          <DialogHeader><DialogTitle className="font-display text-xl">{profile?.name}</DialogTitle>
            <DialogDescription className="text-zinc-500">{profile?.role_name || profile?.role} · {profile?.department || "—"}</DialogDescription></DialogHeader>
          {profile && (
            <div className="pt-1">
              <div className="flex gap-1 border-b border-white/10 mb-4">
                {["overview", "access", "responsibilities"].map((x) => (
                  <button key={x} data-testid={`profile-tab-${x}`} onClick={() => setTab(x)} className={`px-3 py-2 text-xs font-mono uppercase tracking-wider border-b-2 -mb-px ${tab === x ? "border-white text-white" : "border-transparent text-zinc-500 hover:text-zinc-300"}`}>{x}</button>
                ))}
              </div>
              {tab === "overview" && (
                <dl className="space-y-2 text-sm">
                  {[["Employee ID", profile.employee_id], ["Email", profile.email], ["Phone", profile.phone], ["Designation", profile.designation], ["Reporting manager", profile.reporting_manager], ["Status", profile.status], ["Active tasks", profile.active_tasks], ["Joined", (profile.joining_date || "").slice(0, 10)]].filter(([, v]) => v || v === 0).map(([k, v]) => (
                    <div key={k} className="flex justify-between gap-3"><dt className="text-zinc-600">{k}</dt><dd className="text-zinc-300 text-right">{String(v)}</dd></div>
                  ))}
                </dl>
              )}
              {tab === "access" && (
                <div className="space-y-4 text-sm">
                  <div><div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-1">Assigned brands</div><div className="text-zinc-300">{(profile.assigned_brands || []).join(", ") || "All / none"}</div></div>
                  <div><div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-1">Assigned hubs</div><div className="text-zinc-300">{(profile.assigned_hubs || []).map((h) => hubs.find((x) => x.id === h)?.name || h).join(", ") || "—"}</div></div>
                  <div><div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-2">Module permissions</div>
                    <div className="flex flex-wrap gap-1.5">
                      {Object.entries(profile.permissions || {}).filter(([, v]) => v && v !== "none").map(([m, l]) => (
                        <span key={m} className="text-[10px] font-mono border border-white/10 rounded-sm px-1.5 py-0.5 text-zinc-300">{m.replace(/_/g, " ")} · {l}</span>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              {tab === "responsibilities" && (
                <ul className="space-y-1.5 text-sm text-zinc-300 list-disc pl-4">
                  {(profile.responsibilities || []).map((r, i) => <li key={i}>{r}</li>)}
                  {(profile.responsibilities || []).length === 0 && <span className="text-xs text-zinc-600 list-none">No responsibilities assigned.</span>}
                </ul>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
